import { useAtomsContext } from "@kolaymeeting/atoms/hooks/useAtomsContext";
import { AppRouterI18nContext } from "@kolaymeeting/dashboard/app/AppRouterI18nProvider";
import { CustomI18nContext } from "@kolaymeeting/dashboard/app/CustomI18nProvider";
import type { i18n, TFunction } from "i18next";
import { createInstance } from "i18next";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

type useLocaleReturnType = {
  i18n: i18n;
  t: TFunction;
  isLocaleReady: boolean;
};

// @internal
const useClientLocale = (
  namespace: Parameters<typeof useTranslation>[0] = "common",
  i18nInstance?: i18n
): useLocaleReturnType => {
  const context = useAtomsContext();
  // Passing the instance explicitly stops react-i18next from falling back to the uninitialized
  // global instance (and warning about initReactI18next) on App Router pages.
  const { i18n, t } = useTranslation(namespace, { i18n: i18nInstance });
  const isLocaleReady = Object.keys(i18n).length > 0;
  if (context?.clientId) {
    return { i18n: context.i18n, t: context.t, isLocaleReady: true } as unknown as useLocaleReturnType;
  }
  return {
    i18n,
    t,
    isLocaleReady,
  };
};

// @internal
const serverI18nInstances = new Map<string, useLocaleReturnType>();

const getServerI18nInstance = ({
  translations,
  locale,
  ns,
}: {
  translations: Record<string, string>;
  locale: string;
  ns: string;
}): useLocaleReturnType => {
  const instanceKey = `${locale}-${ns}`;
  const existing = serverI18nInstances.get(instanceKey);
  if (existing) return existing;

  const i18n = createInstance();
  i18n.init({
    lng: locale,
    resources: {
      [locale]: {
        [ns]: translations,
      },
    },
  });

  const instance = {
    t: i18n.getFixedT(locale, ns),
    isLocaleReady: true,
    i18n,
  };
  serverI18nInstances.set(instanceKey, instance);
  return instance;
};

export const useLocale = (): useLocaleReturnType => {
  const appRouterContext = useContext(AppRouterI18nContext);
  const customI18nContext = useContext(CustomI18nContext);
  const activeContext = appRouterContext ? (customI18nContext ?? appRouterContext) : null;
  const serverInstance = activeContext ? getServerI18nInstance(activeContext) : null;
  const clientI18n = useClientLocale(activeContext?.ns, serverInstance?.i18n);

  if (serverInstance) return serverInstance;

  console.warn(
    "useLocale hook is being used outside of App Router - hence this hook will use a global, client-side i18n which can cause a small flicker"
  );
  return {
    t: clientI18n.t,
    isLocaleReady: clientI18n.isLocaleReady,
    i18n: clientI18n.i18n,
  };
};
